// src/components/DuplicateJobPopup.js
// ---------------------------------------------------------------------------
// "Duplicate" in a job's Supervisor section (Home popup, Home's calendar,
// Calendar page).
//
// Makes a copy of the job under the next free work order (5456 -> 5456-2,
// same numbering as Continue). Unlike Continue, the original is NOT completed:
// both jobs stay Active. See createDuplicateJob in utils/continueJobUtils.js
// for exactly what is and isn't copied.
//
//   <DuplicateJobPopup
//     job={job}
//     createdBy={{ uid, firstName, lastName, fullName }}   // optional
//     onClose={() => ...}
//     onDuplicated={({ id, wo, job }) => ...}               // new job
//   />
// ---------------------------------------------------------------------------

import React, { useEffect, useState } from "react";
import { createDuplicateJob, getNextContinuedWO } from "../utils/continueJobUtils";
import { blockIfViewingAs } from "../utils/viewAs";
import "./DuplicateJobPopup.css";

const dayKey = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  if (v?.toDate) return v.toDate().toISOString().slice(0, 10);
  if (v?.seconds) return new Date(v.seconds * 1000).toISOString().slice(0, 10);
  return "";
};

const DuplicateJobPopup = ({ job, createdBy, onClose, onDuplicated }) => {
  const [startDate, setStartDate] = useState(dayKey(job?.startDate));
  const [nextWO, setNextWO] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getNextContinuedWO(job?.wo)
      .then((wo) => !cancelled && setNextWO(wo))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [job?.wo]);

  if (!job) return null;

  const start = dayKey(job.startDate);
  const end = dayKey(job.endDate);
  const multiDay = start && end && end > start;

  const save = async () => {
    if (saving) return;
    if (blockIfViewingAs()) return; // "View as" is read-only
    setSaving(true);
    setError("");
    try {
      const created = await createDuplicateJob({ sourceJob: job, startDate, createdBy });
      onDuplicated?.(created);
    } catch (err) {
      console.error("Duplicate job failed:", err);
      setError(err?.message || "Could not duplicate this job.");
      setSaving(false);
    }
  };

  return (
    <div className="djp-overlay" onClick={() => !saving && onClose?.()}>
      <div className="djp-card" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="djp-title">Duplicate Job</h3>
        <p className="djp-sub">
          {job.wo ? `WO ${job.wo}` : "No WO"}
          {job.siteName ? ` · ${job.siteName}` : ""}
        </p>

        <div className="djp-warning" role="alert">
          <b>Duplicate makes a copy but both jobs stay active.</b> If you are done with the job for today and it
          will continue another day, click <b>Continue</b> not Duplicate.
        </div>

        <p className="djp-text">
          Creates a new <b>Active</b> job
          {nextWO ? (
            <>
              {" "}as <b>WO {nextWO}</b>
            </>
          ) : (
            " under the next work order"
          )}
          . <b>WO {job.wo || "this job"} stays Active</b> — it is not completed or changed.
        </p>

        <label className="djp-field">
          <span>Start date for the duplicate</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          {multiDay && <small>Keeps the same number of days as the original ({start} – {end}).</small>}
        </label>

        <ul className="djp-list">
          <li>Copied: site, scope, description, Tech Assign, files, location, sub pay terms.</li>
          <li>Starts fresh: notes, receipts, materials list, job total, invoices.</li>
        </ul>

        {error && <p className="djp-error">{error}</p>}

        <div className="djp-actions">
          <button type="button" className="djp-btn djp-btn--primary" onClick={save} disabled={saving}>
            {saving ? "Duplicating…" : "Duplicate"}
          </button>
          <button type="button" className="djp-btn" onClick={() => onClose?.()} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateJobPopup;
